import { officeId, pick } from "./officeHelpers";

export const WEEKDAYS = [
  { day_index: 1, day: "Monday" },
  { day_index: 2, day: "Tuesday" },
  { day_index: 3, day: "Wednesday" },
  { day_index: 4, day: "Thursday" },
  { day_index: 5, day: "Friday" },
  { day_index: 6, day: "Saturday" },
  { day_index: 0, day: "Sunday" },
];

export const DEFAULT_START = "09:00";
export const DEFAULT_END = "17:00";

export function emptyWeek() {
  return WEEKDAYS.map((day) => ({
    ...day,
    id: null,
    open: true,
    start: DEFAULT_START,
    end: DEFAULT_END,
  }));
}

export function toTimeInput(value) {
  if (value == null || value === "") return "";
  const text = String(value).trim();
  const match = text.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  return `${String(match[1]).padStart(2, "0")}:${match[2]}`;
}

export function toTimePayload(value) {
  const hhmm = toTimeInput(value);
  if (!hhmm) return "";
  return `${hhmm}:00`;
}

export function timingRecordId(item) {
  if (!item || typeof item !== "object") return null;
  const id = pick(item, ["id", "pk", "timing_id", "officetiming_id"]);
  return id == null || id === "" ? null : id;
}

export function timingDayIndex(item) {
  if (item == null || typeof item !== "object") return NaN;
  const raw = item.Day ?? item.day ?? item.day_index ?? item.dayIndex;
  const index = Number(raw);
  return Number.isInteger(index) ? index : NaN;
}

export function timingStart(item) {
  return pick(item, ["Start_date", "start", "start_time", "Start"]);
}

export function timingEnd(item) {
  return pick(item, ["End_date", "end", "end_time", "End"]);
}

function asTimingList(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value || typeof value !== "object") return [];
  return Object.entries(value)
    .filter(([key]) => !["message", "status", "success"].includes(key))
    .map(([key, item]) => {
      if (!item || typeof item !== "object") return null;
      return { Day: key, ...item };
    })
    .filter(Boolean);
}

export function timingsFromOffice(office) {
  if (!office || typeof office !== "object") return [];
  return asTimingList(
    office.timings ||
      office.timming ||
      office.timing ||
      office.hours ||
      office.officetiming
  );
}

export function weekFromTimings(records) {
  const byIndex = {};
  (records || []).forEach((item) => {
    const index = timingDayIndex(item);
    if (!Number.isInteger(index) || index < 0 || index > 6) return;
    byIndex[index] = item;
  });

  return WEEKDAYS.map((day) => {
    const item = byIndex[day.day_index];
    if (!item) {
      return {
        ...day,
        id: null,
        open: false,
        start: DEFAULT_START,
        end: DEFAULT_END,
      };
    }
    return {
      ...day,
      id: timingRecordId(item),
      open: true,
      start: toTimeInput(timingStart(item)) || DEFAULT_START,
      end: toTimeInput(timingEnd(item)) || DEFAULT_END,
    };
  });
}

export function weekdayLabel(index, display) {
  const fromList = WEEKDAYS.find((day) => day.day_index === Number(index));
  if (display) {
    const text = String(display).trim();
    if (text) {
      return text.charAt(0).toUpperCase() + text.slice(1);
    }
  }
  return fromList?.day || `Day ${index}`;
}

export function listedTimings(office) {
  const order = WEEKDAYS.map((day) => day.day_index);
  return timingsFromOffice(office)
    .map((item) => {
      const day_index = timingDayIndex(item);
      return {
        id: timingRecordId(item),
        day_index,
        day: weekdayLabel(day_index, item.day_display || item.day),
        start: toTimeInput(timingStart(item)),
        end: toTimeInput(timingEnd(item)),
      };
    })
    .filter((item) => Number.isInteger(item.day_index))
    .sort(
      (a, b) => order.indexOf(a.day_index) - order.indexOf(b.day_index)
    );
}

export function missingWeekdays(office) {
  const present = new Set(
    listedTimings(office).map((item) => item.day_index)
  );
  return WEEKDAYS.filter((day) => !present.has(day.day_index));
}

export function weekFromOffice(office) {
  return weekFromTimings(timingsFromOffice(office));
}

export function findOfficeInListing(payload, id) {
  const lists = [
    payload,
    payload?.data,
    payload?.results,
    payload?.offices,
    payload?.data?.results,
    payload?.data?.offices,
    payload?.data?.data,
  ];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    const match = list.find((office) => String(officeId(office)) === String(id));
    if (match) return match;
  }
  return null;
}

export function timingPayload(officePk, row) {
  return {
    office: Number(officePk) || officePk,
    Day: String(row.day_index),
    Start_date: toTimePayload(row.start),
    End_date: toTimePayload(row.end),
  };
}

export function timesAreValid(start, end) {
  const a = toTimePayload(start);
  const b = toTimePayload(end);
  return Boolean(a && b && a < b);
}
